/**
 * Celestial Dashboard - Full Enochian Astronomical System View
 * Features 6-Gate Sun diagram, Moon phase tracker, and curriculum sync
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import SkyMap from "./SkyMap";
import InvisibleTeacher from "./InvisibleTeacher";
import { useCelestial } from "../contexts/CelestialContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CelestialDashboard({ onCurriculumLock = null, gradeLevel = "6-8", user, onBack = null }) {
  // Centralized celestial data (single source of truth across the app).
  const { celestialData, location } = useCelestial();
  const [gatesInfo, setGatesInfo] = useState(null);
  const [feastData, setFeastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch supplementary endpoints that aren't in /celestial/data (gates + feasts).
  const fetchSupplementary = useCallback(async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [gates, feasts] = await Promise.all([
        axios.get(`${API}/celestial/gates`, { headers }),
        axios.get(`${API}/celestial/feasts`, {
          params: { latitude: location.lat, longitude: location.lon },
          headers,
        }),
      ]);
      setGatesInfo(gates.data);
      setFeastData(feasts.data);
    } catch (err) {
      console.error("Failed to fetch celestial supplementary data:", err);
    } finally {
      setLoading(false);
    }
  }, [location.lat, location.lon]);

  useEffect(() => {
    fetchSupplementary();
    const interval = setInterval(fetchSupplementary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSupplementary]);

  // Curriculum-lock callback hooks into the centralized data so it
  // fires the moment the High Sabbath flips on at sundown.
  useEffect(() => {
    if (celestialData?.enochian?.is_high_sabbath && onCurriculumLock) {
      onCurriculumLock(true);
    }
  }, [celestialData, onCurriculumLock]);

  // Generate celestial teaching content for the Invisible Teacher
  // Must be called before early returns to maintain hooks order
  // ── 2026-02-21 USER MANDATE ────────────────────────────────────
  // "Currently, when the play button is engaged, it only resumes at
  // section four and does not provide an overview or explanation of
  // the current day's real-time data across the Overview, Sky Map,
  // Gates, Moon, and Feasts tabs. Furthermore, the dashboard lacks
  // navigation buttons such as Restart or Previous."
  //
  // Fix: the Invisible Teacher now reads tab-specific narration of
  // the LIVE celestial data, regenerated on every tab switch. The
  // lesson "slug" is keyed off the active tab so progress in one tab
  // doesn't bleed into another (no more stale "Resuming at section 4"
  // when the user just switched from Overview to Moon).
  const celestialLessonContent = useMemo(() => {
    if (!celestialData || !celestialData.biblical) return "";

    const { biblical, enochian: eno, sun: sunData, moon: moonData } = celestialData;
    const biblicalDate = biblical?.date || {};
    const sabbath = biblical?.sabbath || {};
    const upcoming = biblical?.moadim?.upcoming || [];

    const dayPhrase = `Day ${biblicalDate.day || 1} of ${biblicalDate.month_name || "the month"}`;
    const sections = [];

    // Always start with a greeting that confirms the live date so the
    // student knows the audio is in sync with TODAY.
    sections.push(
      `Shalom, Nation of Yisrael. Today is ${dayPhrase}. ` +
      `You are currently viewing the ${activeTab} tab of the Celestial Observatory.`
    );

    if (activeTab === "overview") {
      // OVERVIEW — sun gate, moon phase, sabbath/feast, day in cycle
      if (eno?.gate_info) {
        sections.push(
          `The Sun is currently passing through ${eno.gate_info.name}. ` +
          `${eno.gate_info.description || "This gate marks the seasonal threshold for solar progression."}`
        );
      }
      if (moonData?.phase_name) {
        sections.push(
          `The Moon is in its ${moonData.phase_name} phase, ` +
          `with ${(moonData.illumination ?? 0).toFixed(1)} percent of its face illuminated. ` +
          `On the fourteen parts of light scale from Enoch chapter 73, we are at ${eno?.moon_parts_of_light ?? 0} parts.`
        );
      }
      if (sabbath?.is_sabbath) {
        sections.push(
          "Today is a Sabbath. Remember the Sabbath day and keep it set apart. Cease your work and rest in the presence of the Creator."
        );
      } else if (sabbath?.is_preparation) {
        sections.push(
          "Today is a Preparation Day. The Sabbath begins at sundown. Complete your studies and ready your household."
        );
      } else if (typeof sabbath?.days_to_sabbath === "number") {
        sections.push(
          `The next Sabbath arrives in ${sabbath.days_to_sabbath} days, ` +
          `from sundown Friday to sundown Saturday on the Gregorian week.`
        );
      }
      const nextMoed = upcoming[0];
      if (nextMoed) {
        sections.push(
          `The next appointed time, or moed, is ${nextMoed.name} — coming in ${nextMoed.days_until} days. ` +
          `${nextMoed.description || ""}`
        );
      }
    } else if (activeTab === "skymap") {
      // SKY MAP — sun & moon positions for the AR viewport
      sections.push(
        `The Sky Map shows the live positions of the Sun and Moon as they sit in the firmament above you right now.`
      );
      if (sunData) {
        const az = sunData.azimuth?.toFixed?.(1) ?? "—";
        const alt = sunData.altitude?.toFixed?.(1) ?? "—";
        sections.push(
          `The Sun's azimuth is ${az} degrees, with an altitude of ${alt} degrees ` +
          `${sunData.altitude > 0 ? "above" : "below"} the horizon. ` +
          `Its ecliptic longitude is ${sunData.ecliptic_longitude?.toFixed?.(1) ?? sunData.longitude?.toFixed?.(1) ?? "currently being calculated"} degrees.`
        );
      }
      if (moonData) {
        const maz = moonData.azimuth?.toFixed?.(1) ?? "—";
        const malt = moonData.altitude?.toFixed?.(1) ?? "—";
        sections.push(
          `The Moon's azimuth is ${maz} degrees, with an altitude of ${malt} degrees ` +
          `${moonData.altitude > 0 ? "above" : "below"} the horizon. Use your device's AR mode to physically locate it in the sky.`
        );
      }
      sections.push(
        "If your sky is overcast, the AR viewport still confirms exactly where each luminary is — the firmament tells the truth even when the clouds do not."
      );
    } else if (activeTab === "gates") {
      // GATES — the 12 gates of Enoch
      if (eno?.gate_info) {
        sections.push(
          `Right now the Sun rises through ${eno.gate_info.name}. ` +
          `${eno.gate_info.description || "This is one of the twelve gates from which the Sun rises and sets according to the seasons."}`
        );
        if (eno.gate_info.season) {
          sections.push(`This gate corresponds to the season of ${eno.gate_info.season}.`);
        }
      }
      sections.push(
        "Enoch chapters 72 through 74 describe twelve gates in the firmament. " +
        "The Sun passes through three gates each season, rising further north in summer and further south in winter. " +
        "By tracking which gate the Sun rises from on any given day, the ancients kept perfect time without any mechanical clock."
      );
    } else if (activeTab === "moon") {
      // MOON — phase, age, parts of light, illumination
      if (moonData) {
        sections.push(
          `The Moon is currently in its ${moonData.phase_name || "current"} phase. ` +
          `It is ${(moonData.illumination ?? 0).toFixed(1)} percent illuminated, ` +
          `and roughly ${(moonData.age_days ?? eno?.lunar_day ?? 0).toFixed?.(1) ?? moonData.age_days ?? 0} days into the current lunar cycle.`
        );
      }
      sections.push(
        `On the Enoch fourteen parts of light scale, we are at ${eno?.moon_parts_of_light ?? 0} parts. ` +
        `Each part represents one fourteenth of full illumination. ` +
        `The Moon waxes from zero parts at the new moon up to fourteen parts at the full moon, ` +
        `then wanes back to zero — completing one lunar cycle of approximately twenty-nine and a half days.`
      );
      if (eno?.current_lunar_day) {
        sections.push(
          `Today is the ${eno.current_lunar_day}${eno.current_lunar_day === 1 ? "st" : eno.current_lunar_day === 2 ? "nd" : eno.current_lunar_day === 3 ? "rd" : "th"} ` +
          `day of the current lunar month. Day one always begins at the first visible crescent — no light, no day one.`
        );
      }
    } else if (activeTab === "feasts") {
      // FEASTS — the upcoming moadim list
      sections.push(
        "The appointed times of the Creator, called the moadim, are fixed on the 364-day calendar. " +
        "They are not adjusted to civil clocks; they arrive in their own season according to Leviticus chapter 23."
      );
      if (upcoming.length > 0) {
        upcoming.slice(0, 4).forEach((m) => {
          sections.push(
            `${m.name} arrives in ${m.days_until} days, on day ${m.day} of month ${m.month}. ` +
            `${m.description || ""}`
          );
        });
      } else {
        sections.push("All upcoming moadim are being computed from the live ephemeris. Check back shortly.");
      }
    }

    // Closing line common to every tab.
    sections.push(
      "The luminaries were placed in the firmament for signs, for seasons, for days, and for years. " +
      "Tap any tab to study a different aspect of the live sky, or press Restart to hear this section again."
    );

    return sections.join("\n\n");
  }, [celestialData, activeTab]);

  // Restart counter — bumping this remounts the InvisibleTeacher and
  // clears the saved section pointer in localStorage for the current
  // tab's lesson slug. Wired to the Restart button below.
  const [teacherRestartKey, setTeacherRestartKey] = useState(0);
  const activeLessonTitle = `Celestial Observatory — ${activeTab}`;
  const TAB_ORDER = ["overview", "skymap", "gates", "moon", "feasts"];
  const tabIdx = TAB_ORDER.indexOf(activeTab);

  const handleTeacherRestart = () => {
    try {
      const slug = activeLessonTitle.trim().toLowerCase().replace(/\s+/g, "_");
      if (slug) localStorage.removeItem(`it_lesson_pos:${slug}`);
    } catch {
      /* localStorage may be unavailable */
    }
    setTeacherRestartKey((k) => k + 1);
  };

  const handleTeacherPrevTab = () => {
    if (tabIdx > 0) {
      setActiveTab(TAB_ORDER[tabIdx - 1]);
      // Switching tabs gives a fresh lesson slug; force a remount so
      // the teacher reads the new tab from the top.
      setTeacherRestartKey((k) => k + 1);
    }
  };

  const handleTeacherNextTab = () => {
    if (tabIdx >= 0 && tabIdx < TAB_ORDER.length - 1) {
      setActiveTab(TAB_ORDER[tabIdx + 1]);
      setTeacherRestartKey((k) => k + 1);
    }
  };

  // Reusable Back button - rendered in EVERY render path (loading,
  // failed, success) so the user is never stuck inside the Observatory
  // without an escape hatch. User mandate: "Add Working navigation
  // buttons to celestial observatory".
  const navBackButton = onBack ? (
    <button
      data-testid="celestial-back-btn"
      onClick={onBack}
      style={{
        position: "absolute",
        top: "15px",
        right: "15px",
        background: "transparent",
        color: "#FFD700",
        border: "1px solid #FFD70080",
        borderRadius: "6px",
        padding: "8px 16px",
        fontSize: "0.85rem",
        cursor: "pointer",
        fontWeight: "bold",
        whiteSpace: "nowrap",
        zIndex: 5,
      }}
    >
      ← Back to Dashboard
    </button>
  ) : null;

  if (loading) {
    return (
      <div style={{ position: "relative", textAlign: "center", padding: "50px" }}>
        {navBackButton}
        <div style={{ fontSize: "3rem", marginBottom: "20px" }}>🌌</div>
        <p style={{ color: "#888" }}>Loading Celestial Data...</p>
      </div>
    );
  }

  if (!celestialData) {
    return (
      <div style={{ position: "relative", textAlign: "center", padding: "50px", color: "#ff6b6b" }}>
        {navBackButton}
        <div style={{ fontSize: "3rem", marginBottom: "20px" }}>🌌</div>
        <p style={{ marginBottom: "20px" }}>Unable to load celestial data right now.</p>
        <button
          data-testid="celestial-retry-btn"
          onClick={() => window.location.reload()}
          style={{
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            color: "#000",
            padding: "10px 22px",
            border: "none",
            borderRadius: "8px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          🔄 Retry
        </button>
      </div>
    );
  }

  const { sun, moon, enochian } = celestialData;
  const biblical = celestialData.biblical || {};
  const biblicalDate = biblical.date || {};
  const sabbathInfo = biblical.sabbath || {};

  // ────────────────────────────────────────────────────────────
  // DAY-14 / SABBATH DISPLAY OVERRIDES (surgical view-only layer)
  // Backend calendar engine is untouched — these are presentational
  // mappings to keep the dashboard's natural language aligned with
  // the lunar cycle (Day 14 = Full Moon Witness, preparing for the
  // Day 15 High Sabbath).
  // ────────────────────────────────────────────────────────────
  const GATE_ORDINALS = { 1: "First", 2: "Second", 3: "Third", 4: "Fourth", 5: "Fifth", 6: "Sixth" };
  const gateOrdinal = GATE_ORDINALS[enochian.current_gate] || `Gate ${enochian.current_gate}`;
  // iter 67 — surface the live ascending/descending direction the
  // backend now ships on `enochian.gate_direction`. e.g.
  // "Third Gate Descending Transition" → "Autumnal Equinox".
  const gateDirection = enochian.gate_direction || "";
  const gateDisplayName = gateDirection
    ? `${gateOrdinal} Gate ${gateDirection} Transition`
    : `${gateOrdinal} Gate Transition`;
  const gateDisplaySubtitle = enochian.gate_phase_label
    ? enochian.gate_phase_label.replace(/^Gate \d+ — /, "")
    : `Gate ${enochian.current_gate} • Solar Progression`;

  const isFullMoonWitness = moon.illumination >= 95 || (enochian.current_lunar_day && enochian.current_lunar_day >= 14 && enochian.current_lunar_day <= 16);
  const moonDisplayPhase = isFullMoonWitness ? "Full Moon Witness" : moon.phase_name;

  const lunarDay = biblicalDate.day ?? enochian.current_lunar_day;
  // 2026-06-05: the misleading "Weekly Sabbath" fallback label was
  // when biblicalDate.month wasn't loaded yet — it implied a non-
  // existent weekly-only cycle layer. The Watchman dashboard already
  // uses "Month N" / "Ziv" cleanly. Mirror that here.
  const biblicalMonthLabel = biblicalDate.month
    ? `Month ${biblicalDate.month}${biblicalDate.month_name ? ` (${biblicalDate.month_name})` : ""}`
    : "—";

  // Preparation cadence — Master 7-day loop: Sabbath = day % 7 === 0
  // (Days 7, 14, 21, 28). Preparation is the day BEFORE → 6, 13, 20, 27.
  // 2026-06-05: shifted from the old 7/14/21/28 prep cadence per user
  // doctrinal update ("Keep the Master 7-day loop pristine from Day 1").
  const isPreparationDay = sabbathInfo.is_preparation === true || [6, 13, 20, 27].includes(lunarDay);
  let curriculumStatusLabel;
  let curriculumStatusSubtext;
  if (lunarDay === 14) {
    // User spec: Day 14 = Preparation Day for Day 15 High Sabbath
    curriculumStatusLabel = "Preparation Day";
    curriculumStatusSubtext = "Preparing for Day 15 High Sabbath";
  } else if (enochian.is_high_sabbath) {
    curriculumStatusLabel = "High Sabbath";
    curriculumStatusSubtext = "Rest & Feast Module";
  } else if (isPreparationDay) {
    curriculumStatusLabel = "Preparation Day";
    curriculumStatusSubtext = "Preparing for the Sabbath";
  } else {
    curriculumStatusLabel = "Curriculum Active";
    curriculumStatusSubtext = "Regular lessons available";
  }

  // Days to next WEEKLY Sabbath — Gregorian Friday-sundown rule
  // (user mandate 2026-02-11: Sabbath = Friday sundown → Saturday
  // sundown, NOT biblical day 7/14/21/28).
  //
  // The backend's `biblical.sabbath` payload exposes:
  //   - is_sabbath        — true between Fri sundown and Sat sundown
  //   - is_preparation    — true on Fri BEFORE sundown
  //   - days_to_sabbath   — 0 on Fri/Sat, else 1..6 (whole days to next Fri)
  //   - gregorian_weekday — 0=Mon..6=Sun
  //   - gregorian_weekday_name — "Friday" / "Saturday" / …
  // We surface "TODAY"/"TONIGHT"/"1 Day"/"N Days" so the dashboard
  // never claims the Sabbath has begun when the user is still in
  // Preparation Day.
  let daysToSabbathDisplay;
  let daysToSabbathSubtext = "(Fri sundown → Sat sundown)";
  const sabBlock = celestialData?.biblical?.sabbath;
  if (sabBlock) {
    const d = sabBlock.days_to_sabbath;
    if (sabBlock.is_sabbath) {
      daysToSabbathDisplay = "TODAY";
    } else if (sabBlock.is_preparation) {
      // Friday before sundown — Sabbath begins at sundown tonight.
      daysToSabbathDisplay = "TONIGHT";
    } else if (typeof d === "number") {
      daysToSabbathDisplay = d === 1 ? "1 Day" : `${d} Days`;
    }
  } else if (typeof lunarDay === "number") {
    // Defensive fallback — should not be reached now that the
    // backend always returns sabbath fields.
    daysToSabbathDisplay = "—";
  }

  // Numbered day-of-week (Sun=1 … Sat=7). User mandate 2026-02-11d:
  // "put the corresponding numbered day of the week for repeated day".
  // Backend `gregorian_weekday` is 0=Mon..6=Sun — map to 1..7 where
  // Sun=1, Mon=2, …, Sat=7 (biblical week ordering).
  let gregorianDayNumber = null;
  let gregorianDayName = null;
  if (sabBlock && typeof sabBlock.gregorian_weekday === "number") {
    const bw = sabBlock.gregorian_weekday;
    // Mon(0)→2, Tue(1)→3, Wed(2)→4, Thu(3)→5, Fri(4)→6, Sat(5)→7, Sun(6)→1
    gregorianDayNumber = bw === 6 ? 1 : bw + 2;
    gregorianDayName = sabBlock.gregorian_weekday_name || null;
  }

  return (
    <div
      data-testid="celestial-dashboard"
      style={{
        background: "linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)",
        borderRadius: "15px",
        overflow: "hidden",
        border: "1px solid #333",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(90deg, #1a1a3a 0%, #2a2a4a 100%)",
          padding: "20px 25px",
          borderBottom: "1px solid #444",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "15px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: "200px" }}>
          <h2 style={{ color: "#FFD700", margin: 0, fontFamily: "Georgia, serif" }}>
            Celestial Observatory
          </h2>
          <p style={{ color: "#888", margin: "5px 0 0", fontSize: "0.85rem" }}>
            Enochian Astronomical System • Real-time Sky Tracking
          </p>
        </div>
        {/* Observatory navigation — user mandate: explicit working
            navigation buttons inside the Celestial Observatory so
            students never feel "stuck" in it. */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {onBack && (
            <button
              data-testid="celestial-back-btn"
              onClick={onBack}
              style={{
                background: "transparent",
                color: "#FFD700",
                border: "1px solid #FFD70080",
                borderRadius: "6px",
                padding: "8px 16px",
                fontSize: "0.85rem",
                cursor: "pointer",
                fontWeight: "bold",
                whiteSpace: "nowrap",
              }}
            >
              ← Back to Dashboard
            </button>
          )}
          <button
            data-testid="celestial-top-btn"
            onClick={() => {
              setActiveTab("overview");
              if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            style={{
              background: "transparent",
              color: "#87CEEB",
              border: "1px solid #87CEEB80",
              borderRadius: "6px",
              padding: "8px 16px",
              fontSize: "0.85rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            🌌 Overview
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #333",
          background: "#111",
        }}
      >
        {["overview", "skymap", "gates", "moon", "feasts"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: "12px",
              background: activeTab === tab ? "#1a1a3a" : "transparent",
              border: "none",
              borderBottom: activeTab === tab ? "2px solid #FFD700" : "2px solid transparent",
              color: activeTab === tab ? "#FFD700" : "#888",
              cursor: "pointer",
              textTransform: "capitalize",
              fontWeight: activeTab === tab ? "bold" : "normal",
            }}
          >
            {tab === "overview" && "🌌 "}
            {tab === "skymap" && "🗺️ "}
            {tab === "gates" && "☀️ "}
            {tab === "moon" && "🌙 "}
            {tab === "feasts" && "🕯️ "}
            {tab === "skymap" ? "Sky Map" : tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: "25px" }}>
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div>
            {/* Current State Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "25px",
              }}
            >
              {/* Sun Position */}
              <div
                style={{
                  background: "linear-gradient(135deg, #3a3a1a 0%, #2a2a0a 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #FFD70060",
                }}
              >
                <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>☀️</div>
                <h4 style={{ color: "#FFD700", margin: "0 0 5px" }} data-testid="sun-gate-label">
                  {gateDisplayName}
                </h4>
                <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }} data-testid="sun-gate-subtitle">
                  {gateDisplaySubtitle}
                </p>
                <p style={{ color: "#666", fontSize: "0.75rem", margin: "4px 0 0" }}>
                  Altitude: {sun.altitude}°
                </p>
              </div>

              {/* Moon Phase */}
              <div
                style={{
                  background: "linear-gradient(135deg, #1a1a3a 0%, #0a0a2a 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #87CEEB60",
                }}
              >
                <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                  {moon.illumination < 5
                    ? "🌑"
                    : moon.illumination < 50
                    ? moon.is_waxing
                      ? "🌓"
                      : "🌗"
                    : moon.illumination < 95
                    ? moon.is_waxing
                      ? "🌔"
                      : "🌖"
                    : "🌕"}
                </div>
                <h4 style={{ color: "#87CEEB", margin: "0 0 5px" }} data-testid="moon-phase-label">{moonDisplayPhase}</h4>
                <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }}>
                  {enochian.moon_parts_of_light} Parts • {moon.illumination.toFixed(1)}%
                </p>
              </div>

              {/* Sabbath Day - Using SVG instead of emoji to avoid device-specific rendering */}
              <div
                style={{
                  background: "linear-gradient(135deg, #2a1a2a 0%, #1a0a1a 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #DDA0DD60",
                }}
              >
                <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#DDA0DD" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                    <text x="12" y="18" textAnchor="middle" fill="#DDA0DD" fontSize="8" fontWeight="bold">{lunarDay ?? enochian.sabbath_count_day}</text>
                  </svg>
                </div>
                <h4 style={{ color: "#DDA0DD", margin: "0 0 5px" }} data-testid="sabbath-day-label">
                  Day {lunarDay ?? enochian.sabbath_count_day}
                </h4>
                <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }} data-testid="sabbath-day-subtitle">
                  of {biblicalMonthLabel}
                </p>
              </div>

              {/* Curriculum Status */}
              {(() => {
                const showAsHighSabbath = curriculumStatusLabel === "High Sabbath";
                const showAsPreparation = curriculumStatusLabel === "Preparation Day";
                return (
                  <div
                    style={{
                      background: showAsHighSabbath
                        ? "linear-gradient(135deg, #3a2a1a 0%, #2a1a0a 100%)"
                        : showAsPreparation
                        ? "linear-gradient(135deg, #3a2a1a 0%, #2a1a0a 100%)"
                        : "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)",
                      borderRadius: "12px",
                      padding: "20px",
                      border: `1px solid ${showAsHighSabbath || showAsPreparation ? "#FFA500" : "#90EE90"}60`,
                    }}
                  >
                    <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>
                      {showAsHighSabbath ? "🕊️" : showAsPreparation ? "🌾" : "📚"}
                    </div>
                    <h4
                      style={{
                        color: showAsHighSabbath || showAsPreparation ? "#FFA500" : "#90EE90",
                        margin: "0 0 5px",
                      }}
                      data-testid="curriculum-status-label"
                    >
                      {curriculumStatusLabel}
                    </h4>
                    <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }} data-testid="curriculum-status-subtitle">
                      {curriculumStatusSubtext}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Quick Stats Row */}
            <div
              data-testid="watchman-quick-stats"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
                background: "#111",
                borderRadius: "10px",
                padding: "15px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                {/* Next-Mo'ed countdown. 2026-06-05: previously this
                    tile bound directly to `moadim.days_to_passover`
                    which silently rolls over to NEXT year's Passover
                    once this year's has passed — producing the
                    "319/320 days" preview bug on a Day-19-of-Ziv
                    screen. The Watchman dashboard correctly shows
                    `upcoming[0]` (the next feast — Shavuot at 27d).
                    Bind to the same `upcoming[0]` so all dashboard
                    previews agree across the app. */}
                <div style={{ color: "#FFD700", fontSize: "1.3rem", fontWeight: "bold" }} data-testid="days-to-next-moed-value">
                  {(() => {
                    const up = celestialData?.biblical?.moadim?.upcoming;
                    if (Array.isArray(up) && up.length > 0) {
                      return `${up[0].days_until}d`;
                    }
                    return celestialData?.biblical?.moadim?.days_to_passover ?? enochian.days_to_14th.toFixed(1);
                  })()}
                </div>
                <div style={{ color: "#888", fontSize: "0.75rem" }}>
                  {(() => {
                    const up = celestialData?.biblical?.moadim?.upcoming;
                    if (Array.isArray(up) && up.length > 0) {
                      return `Days to ${up[0].name}`;
                    }
                    return "Days to Passover";
                  })()}
                </div>
              </div>
              <div style={{ textAlign: "center", borderLeft: "1px solid #333", borderRight: "1px solid #333" }}>
                <div
                  style={{
                    color: daysToSabbathDisplay === "TODAY" ? "#FFD700" : daysToSabbathDisplay === "TONIGHT" ? "#CD853F" : "#87CEEB",
                    fontSize: "1.3rem",
                    fontWeight: "bold",
                  }}
                  data-testid="days-to-sabbath-value"
                >
                  {daysToSabbathDisplay ?? "—"}
                </div>
                <div style={{ color: "#888", fontSize: "0.75rem" }}>Days to Sabbath</div>
                <div style={{ color: "#555", fontSize: "0.65rem", marginTop: 2 }}>
                  {daysToSabbathSubtext}
                </div>
              </div>
              <div style={{ textAlign: "center" }} data-testid="watchman-weekday-tile">
                <div style={{ color: "#90EE90", fontSize: "1.3rem", fontWeight: "bold" }}>
                  {gregorianDayNumber ?? "—"}
                </div>
                <div style={{ color: "#888", fontSize: "0.75rem" }}>Day of Week</div>
                {gregorianDayName && (
                  <div style={{ color: "#555", fontSize: "0.65rem", marginTop: 2 }}>
                    {gregorianDayName}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "center", borderLeft: "1px solid #333" }}>
                <div style={{ color: "#DDA0DD", fontSize: "1.3rem", fontWeight: "bold" }}>
                  {lunarDay ?? Math.min(Math.floor(moon.age_days - 0.5) + 1, 30)}
                </div>
                <div style={{ color: "#888", fontSize: "0.75rem" }}>Day of Month</div>
              </div>
            </div>
          </div>
        )}

        {/* Sky Map Tab */}
        {activeTab === "skymap" && (
          <div>
            <SkyMap celestialData={celestialData} compact={false} />
          </div>
        )}

        {/* Gates Tab - 6-Gate Visual System */}
        {activeTab === "gates" && gatesInfo && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <h3 style={{ color: "#FFD700", marginBottom: "10px" }}>
                The 6-Gate Solar System
              </h3>
              <p style={{ color: "#888", fontSize: "0.9rem", maxWidth: "500px", margin: "0 auto" }}>
                {gatesInfo.description}
              </p>
            </div>

            {/* Visual Gate Diagram */}
            <div
              style={{
                position: "relative",
                width: "300px",
                height: "300px",
                margin: "0 auto 30px",
                borderRadius: "50%",
                background: "radial-gradient(circle, #1a1a2e 0%, #0a0a1a 100%)",
                border: "2px solid #333",
              }}
            >
              {/* Sun in center */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "3rem",
                  zIndex: 10,
                }}
              >
                ☀️
              </div>

              {/* Gate markers */}
              {[1, 2, 3, 4, 5, 6].map((gate, index) => {
                const angle = ((index * 60) - 90) * (Math.PI / 180);
                const radius = 120;
                const x = 150 + radius * Math.cos(angle);
                const y = 150 + radius * Math.sin(angle);
                const isCurrentGate = gate === enochian.current_gate;

                return (
                  <div
                    key={gate}
                    style={{
                      position: "absolute",
                      left: `${x}px`,
                      top: `${y}px`,
                      transform: "translate(-50%, -50%)",
                      width: isCurrentGate ? "50px" : "40px",
                      height: isCurrentGate ? "50px" : "40px",
                      borderRadius: "50%",
                      background: isCurrentGate
                        ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                        : "#222",
                      border: `2px solid ${isCurrentGate ? "#FFD700" : "#444"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: isCurrentGate ? "#000" : "#888",
                      fontWeight: "bold",
                      fontSize: isCurrentGate ? "1.1rem" : "0.9rem",
                      transition: "all 0.3s ease",
                      boxShadow: isCurrentGate ? "0 0 20px #FFD70080" : "none",
                    }}
                  >
                    {gate}
                  </div>
                );
              })}

              {/* Current gate indicator text */}
              <div
                style={{
                  position: "absolute",
                  bottom: "-40px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#FFD700",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                Currently in Gate {enochian.current_gate}
              </div>
            </div>

            {/* Gate Details Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "10px",
                marginTop: "50px",
              }}
            >
              {Object.entries(gatesInfo.gates).map(([num, info]) => (
                <div
                  key={num}
                  style={{
                    background:
                      parseInt(num) === enochian.current_gate
                        ? "linear-gradient(135deg, #3a3a1a 0%, #2a2a0a 100%)"
                        : "#111",
                    borderRadius: "8px",
                    padding: "12px",
                    border: `1px solid ${
                      parseInt(num) === enochian.current_gate ? "#FFD700" : "#333"
                    }`,
                  }}
                >
                  <div
                    style={{
                      color: parseInt(num) === enochian.current_gate ? "#FFD700" : "#888",
                      fontWeight: "bold",
                      marginBottom: "5px",
                    }}
                  >
                    Gate {num}
                  </div>
                  <div style={{ color: "#aaa", fontSize: "0.75rem" }}>{info.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Moon Tab */}
        {activeTab === "moon" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "30px" }}>
              <div style={{ fontSize: "4rem", marginBottom: "15px" }}>
                {moon.illumination < 5
                  ? "🌑"
                  : moon.illumination < 25
                  ? moon.is_waxing
                    ? "🌒"
                    : "🌘"
                  : moon.illumination < 50
                  ? moon.is_waxing
                    ? "🌓"
                    : "🌗"
                  : moon.illumination < 75
                  ? moon.is_waxing
                    ? "🌔"
                    : "🌖"
                  : "🌕"}
              </div>
              <h3 style={{ color: "#87CEEB", marginBottom: "5px" }}>{moon.phase_name}</h3>
              <p style={{ color: "#888" }}>
                {moon.illumination.toFixed(1)}% illuminated • {moon.is_waxing ? "Waxing" : "Waning"}
              </p>
            </div>

            {/* 14 Parts Scale */}
            <div style={{ marginBottom: "30px" }}>
              <h4 style={{ color: "#87CEEB", marginBottom: "15px" }}>
                14 Parts of Light ({enochian.moon_parts_of_light} / 14)
              </h4>
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  height: "40px",
                  borderRadius: "20px",
                  overflow: "hidden",
                  background: "#111",
                  padding: "5px",
                }}
              >
                {[...Array(15)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      borderRadius: "5px",
                      background:
                        i <= enochian.moon_parts_of_light
                          ? `hsl(${200 + i * 5}, 70%, ${30 + i * 3}%)`
                          : "#222",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: i <= enochian.moon_parts_of_light ? "#fff" : "#444",
                      fontSize: "0.7rem",
                      fontWeight: i === enochian.moon_parts_of_light ? "bold" : "normal",
                      border:
                        i === enochian.moon_parts_of_light ? "2px solid #87CEEB" : "none",
                    }}
                  >
                    {i}
                  </div>
                ))}
              </div>
            </div>

            {/* Moon Stats */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "15px",
              }}
            >
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "15px",
                  textAlign: "center",
                }}
              >
                <div style={{ color: "#87CEEB", fontSize: "1.5rem", fontWeight: "bold" }}>
                  {moon.age_days.toFixed(1)}
                </div>
                <div style={{ color: "#888", fontSize: "0.8rem" }}>Days Old</div>
              </div>
              <div
                style={{
                  background: "#111",
                  borderRadius: "10px",
                  padding: "15px",
                  textAlign: "center",
                }}
              >
                <div style={{ color: "#87CEEB", fontSize: "1.5rem", fontWeight: "bold" }}>
                  {moon.altitude.toFixed(1)}°
                </div>
                <div style={{ color: "#888", fontSize: "0.8rem" }}>Altitude</div>
              </div>
            </div>

            {/* Next New Moon */}
            <div
              style={{
                marginTop: "20px",
                background: "#1a1a2a",
                borderRadius: "10px",
                padding: "15px",
                textAlign: "center",
                border: "1px solid #333",
              }}
            >
              <div style={{ color: "#888", marginBottom: "5px" }}>Next New Moon</div>
              <div style={{ color: "#FFD700", fontWeight: "bold" }}>
                {new Date(enochian.next_new_moon).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </div>
          </div>
        )}

        {/* Feasts Tab */}
        {activeTab === "feasts" && feastData && (
          <div>
            <h3 style={{ color: "#FFD700", marginBottom: "20px", textAlign: "center" }}>
              Upcoming Feasts & Holy Days
            </h3>

            {/* Current Month Day */}
            <div
              style={{
                background: "#111",
                borderRadius: "12px",
                padding: "20px",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <div style={{ color: "#888", marginBottom: "5px" }}>Current Biblical Day</div>
              <div
                style={{
                  color: "#FFD700",
                  fontSize: "3rem",
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                {feastData.current_moon_day}
              </div>
              <div style={{ color: "#888", marginTop: "5px" }}>
                {feastData.current_moon_month_name ? `of ${feastData.current_moon_month_name} • ` : ""}
                {feastData.moon_parts} parts of light
              </div>
            </div>

            {/* Feast Cards */}
            <div style={{ display: "grid", gap: "15px" }}>
              {/* Passover - 14th Day */}
              <div
                style={{
                  background: feastData.upcoming_feasts.passover_14th.is_today
                    ? "linear-gradient(135deg, #8B0000 0%, #DC143C 100%)"
                    : "linear-gradient(135deg, #2a1a1a 0%, #1a0a0a 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #DC143C60",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h4 style={{ color: "#DC143C", margin: "0 0 5px" }}>
                      🩸 14th Day - Passover
                    </h4>
                    <p style={{ color: "#aaa", margin: 0, fontSize: "0.85rem" }}>
                      {feastData.upcoming_feasts.passover_14th.description}
                    </p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        color: feastData.upcoming_feasts.passover_14th.is_today ? "#fff" : "#DC143C",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                      }}
                    >
                      {feastData.upcoming_feasts.passover_14th.is_today
                        ? "TOMORROW!"
                        : feastData.upcoming_feasts.passover_14th.days_until != null
                        ? `${Number(feastData.upcoming_feasts.passover_14th.days_until).toFixed(0)} days`
                        : "—"}
                    </div>
                    {feastData.upcoming_feasts.passover_14th.gregorian_date && (
                      <div style={{ color: "#aaa", fontSize: "0.75rem", marginTop: "4px" }}>
                        {feastData.upcoming_feasts.passover_14th.gregorian_date}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* High Sabbath - 15th Day */}
              <div
                style={{
                  background: feastData.upcoming_feasts.high_sabbath_15th.is_today
                    ? "linear-gradient(135deg, #8B4513 0%, #CD853F 100%)"
                    : "linear-gradient(135deg, #2a2a1a 0%, #1a1a0a 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid #CD853F60",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <h4 style={{ color: "#CD853F", margin: "0 0 5px" }}>
                      🕊️ 15th Day - High Sabbath
                    </h4>
                    <p style={{ color: "#aaa", margin: 0, fontSize: "0.85rem" }}>
                      {feastData.upcoming_feasts.high_sabbath_15th.description}
                    </p>
                    {feastData.upcoming_feasts.high_sabbath_15th.curriculum_locked && (
                      <span
                        style={{
                          display: "inline-block",
                          background: "#FFA500",
                          color: "#000",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          marginTop: "8px",
                        }}
                      >
                        CURRICULUM PAUSED
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        color: feastData.upcoming_feasts.high_sabbath_15th.is_today
                          ? "#fff"
                          : "#CD853F",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                      }}
                    >
                      {feastData.upcoming_feasts.high_sabbath_15th.is_today
                        ? "TODAY!"
                        : feastData.upcoming_feasts.high_sabbath_15th.days_until != null
                        ? `${Number(feastData.upcoming_feasts.high_sabbath_15th.days_until).toFixed(0)} days`
                        : "—"}
                    </div>
                    {feastData.upcoming_feasts.high_sabbath_15th.gregorian_date && (
                      <div style={{ color: "#aaa", fontSize: "0.75rem", marginTop: "4px" }}>
                        {feastData.upcoming_feasts.high_sabbath_15th.gregorian_date}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Gregorian Weekly Sabbath display — pulled from
                CelestialContext (same source as EnochianWatchman).
                2026-02-11 user mandate: Sabbath = Friday sundown →
                Saturday sundown (Gregorian weekly cycle), independent
                of biblical day-of-month. */}
            <div
              style={{
                marginTop: "25px",
                background: "#111",
                borderRadius: "12px",
                padding: "20px",
              }}
            >
              <h4 style={{ color: "#DDA0DD", marginBottom: "15px" }}>
                Gregorian Weekly Sabbath (Fri sundown → Sat sundown)
              </h4>
              <div style={{ display: "flex", gap: "8px" }}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label, idx) => {
                  const sab = celestialData?.biblical?.sabbath || {};
                  const bw = sab.gregorian_weekday;
                  const todayIdx = bw != null ? (bw === 6 ? 0 : bw + 1) : null;
                  const isToday = todayIdx === idx;
                  const isSabbathCol = idx === 6;
                  const isPrepCol = idx === 5;
                  return (
                    <div
                      key={label}
                      style={{
                        flex: 1,
                        padding: "10px 5px",
                        borderRadius: "8px",
                        background: isToday
                          ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                          : isSabbathCol
                          ? "#2a1a2a"
                          : "#1a1a1a",
                        border: `1px solid ${
                          isToday ? "#FFD700" : isSabbathCol ? "#DDA0DD" : isPrepCol ? "#555" : "#333"
                        }`,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          color: isToday ? "#000" : isSabbathCol ? "#DDA0DD" : "#888",
                          fontWeight: isToday ? "bold" : "normal",
                          fontSize: "1.05rem",
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          color: isToday ? "#000" : "#666",
                          fontSize: "0.6rem",
                          marginTop: "2px",
                        }}
                      >
                        {isSabbathCol ? "Sabbath" : isPrepCol ? "Prep" : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ color: "#666", fontSize: "0.7rem", marginTop: "10px", textAlign: "center" }}>
                Biblical day {celestialData?.biblical?.date?.day} of {celestialData?.biblical?.date?.month_name} (tracked independently)
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* THE INVISIBLE TEACHER - Voice-First AI for Celestial Observatory
          2026-02-21: per-tab lesson slug + restart key so each tab
          starts fresh and the Restart button works as expected. */}
      <InvisibleTeacher
        key={`it-celestial-${activeTab}-${teacherRestartKey}`}
        gradeLevel={gradeLevel}
        lessonContent={celestialLessonContent}
        lessonTitle={activeLessonTitle}
        user={user}
        autoStart={false}
      />

      {/* 2026-02-21 USER MANDATE: Restart / Previous / Next nav for the
          Invisible Teacher inside the Observatory — matching the
          Forensic Etymology pattern. */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          padding: "0 25px 15px",
          flexWrap: "wrap",
        }}
      >
        <button
          data-testid="celestial-teacher-prev-btn"
          onClick={handleTeacherPrevTab}
          disabled={tabIdx <= 0}
          style={{
            background: tabIdx <= 0 ? "#1a1a1a" : "transparent",
            color: tabIdx <= 0 ? "#555" : "#87CEEB",
            border: `1px solid ${tabIdx <= 0 ? "#333" : "#87CEEB80"}`,
            borderRadius: "6px",
            padding: "10px 18px",
            cursor: tabIdx <= 0 ? "not-allowed" : "pointer",
            fontSize: "0.85rem",
            fontWeight: "bold",
          }}
        >
          ← Previous
        </button>
        <button
          data-testid="celestial-teacher-restart-btn"
          onClick={handleTeacherRestart}
          style={{
            background: "transparent",
            color: "#FFA500",
            border: "1px solid #FFA50080",
            borderRadius: "6px",
            padding: "10px 18px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: "bold",
          }}
        >
          ↻ Restart
        </button>
        <button
          data-testid="celestial-teacher-next-btn"
          onClick={handleTeacherNextTab}
          disabled={tabIdx < 0 || tabIdx >= TAB_ORDER.length - 1}
          style={{
            background: tabIdx < 0 || tabIdx >= TAB_ORDER.length - 1 ? "#1a1a1a" : "transparent",
            color: tabIdx < 0 || tabIdx >= TAB_ORDER.length - 1 ? "#555" : "#87CEEB",
            border: `1px solid ${tabIdx < 0 || tabIdx >= TAB_ORDER.length - 1 ? "#333" : "#87CEEB80"}`,
            borderRadius: "6px",
            padding: "10px 18px",
            cursor: tabIdx < 0 || tabIdx >= TAB_ORDER.length - 1 ? "not-allowed" : "pointer",
            fontSize: "0.85rem",
            fontWeight: "bold",
          }}
        >
          Next →
        </button>
      </div>

      {/* Bottom navigation — mirrors the header's nav buttons so the
          student can exit the observatory without scrolling back up. */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          padding: "20px 25px 30px",
          borderTop: "1px solid #333",
          flexWrap: "wrap",
        }}
      >
        {onBack && (
          <button
            data-testid="celestial-back-bottom-btn"
            onClick={onBack}
            style={{
              background: "transparent",
              color: "#FFD700",
              border: "1px solid #FFD70080",
              borderRadius: "6px",
              padding: "10px 22px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            ← Back to Dashboard
          </button>
        )}
        <button
          data-testid="celestial-scroll-top-btn"
          onClick={() => {
            if (typeof window !== "undefined") {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          style={{
            background: "transparent",
            color: "#87CEEB",
            border: "1px solid #87CEEB80",
            borderRadius: "6px",
            padding: "10px 22px",
            cursor: "pointer",
          }}
        >
          ↑ Scroll to Top
        </button>
      </div>
    </div>
  );
}

export default CelestialDashboard;
